"use client";

import { create } from "zustand";
import { persist } from "zustand/middleware";

interface TrackingConsentState {
  // Consent state
  isSessionTrackingEnabled: boolean;
  isCampaignTrackingEnabled: boolean;
  isReferrerTrackingEnabled: boolean;

  // Action methods
  enableAllTracking: () => void;
  disableAllTracking: () => void;
  setSessionTrackingEnabled: (enabled: boolean) => void;
  setCampaignTrackingEnabled: (enabled: boolean) => void;
  setReferrerTrackingEnabled: (enabled: boolean) => void;

  // Utility method
  isAnyTrackingEnabled: () => boolean;
}

// Define a default consent state - in EU countries this should default to false
const DEFAULT_TRACKING_ENABLED = false;

export const useTrackingConsentStore = create<TrackingConsentState>()(
  persist(
    (set, get) => ({
      // Default all tracking to disabled until user consents
      isSessionTrackingEnabled: DEFAULT_TRACKING_ENABLED,
      isCampaignTrackingEnabled: DEFAULT_TRACKING_ENABLED,
      isReferrerTrackingEnabled: DEFAULT_TRACKING_ENABLED,

      // Enable all types of tracking
      enableAllTracking: () =>
        set({
          isSessionTrackingEnabled: true,
          isCampaignTrackingEnabled: true,
          isReferrerTrackingEnabled: true,
        }),

      // Disable all types of tracking
      disableAllTracking: () =>
        set({
          isSessionTrackingEnabled: false,
          isCampaignTrackingEnabled: false,
          isReferrerTrackingEnabled: false,
        }),

      // Setters for individual tracking types
      setSessionTrackingEnabled: (enabled: boolean) =>
        set({ isSessionTrackingEnabled: enabled }),

      setCampaignTrackingEnabled: (enabled: boolean) =>
        set({ isCampaignTrackingEnabled: enabled }),

      setReferrerTrackingEnabled: (enabled: boolean) =>
        set({ isReferrerTrackingEnabled: enabled }),

      // Utility to check if any tracking is enabled
      isAnyTrackingEnabled: () => {
        const state = get();
        return (
          state.isSessionTrackingEnabled ||
          state.isCampaignTrackingEnabled ||
          state.isReferrerTrackingEnabled
        );
      },
    }),
    {
      name: "tracking-consent",
      // Make sure to include the version so you can migrate if the structure changes
      version: 1,
    }
  )
);

/**
 * Utility function to immediately clean up all session tracking data
 * Call this when user revokes consent
 */
export function cleanupTrackingData(): void {
  if (typeof window === "undefined") return;

  // Clear session storage
  sessionStorage.clear();

  // You could also clear specific items if needed rather than everything
  // sessionStorage.removeItem('careindeed_session_data');

  console.log("Tracking data has been cleared due to consent withdrawal");
}
